import { useState, useMemo } from "react";
import { Card, CardHeader, CardBody } from "@heroui/card";
import { Input } from "@heroui/input";
import { Button } from "@heroui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter,} from "@heroui/drawer";
import { Avatar } from "@heroui/avatar";
import { useSession } from "@clerk/clerk-react";
import { addToast } from "@heroui/toast";
import { createUser as createUserApi } from "@/api/users";

const CreateConnectionDrawer = ({ show = false, onResp = () => {} }) => {
const { session } = useSession();

const dummyName = "John Snow";
const dummyMail = "js@starks.thenorth";

const [creating, setCreating] = useState(false);
const [firstName, setFirstName] = useState("");
const [lastName,  setLastName]  = useState("");
const [email,     setEmail]     = useState("");
const [mobileZa,  setMobileZa]  = useState("");

// touched & submit attempt flags
const [touched, setTouched] = useState({ first: false, last: false });
const [submitAttempted, setSubmitAttempted] = useState(false);

const markTouched = (key) =>
    setTouched((t) => ({ ...t, [key]: true }));

// Mobile digits only
const handleMobileChange = (val) => {
    const digits = (val || "").replace(/\D/g, "");
    setMobileZa(digits.slice(0, 10));
};
const mobileDisplay = useMemo(() => mobileZa, [mobileZa]);

// Avatar updates with names
const avatarName = firstName || lastName ? `${firstName} ${lastName}`.trim() : dummyName;

const rawSeed = firstName || lastName ? `${firstName}${lastName}` : dummyMail;

const avatarSeed = encodeURIComponent(rawSeed);

// Validation
const firstInvalid = firstName.trim() === "";
const lastInvalid  = lastName.trim() === "";
const isValid = !firstInvalid && !lastInvalid;

const showFirstError = (touched.first || submitAttempted) && firstInvalid;
const showLastError  = (touched.last  || submitAttempted) && lastInvalid;

async function createMember() {
    setSubmitAttempted(true);
    if (!isValid) return;

    const token = await session.getToken();

    const national = (mobileZa || "").replace(/\D/g, "").replace(/^0/, "");
    const mobileFull = national ? `+27${national}` : "";

    const params = {
        first_name: firstName?.trim(),
        last_name: lastName?.trim(),
        email: email?.trim(),
        mobile: mobileFull,
    };

    setCreating(true);
    createUserApi(params, token).then((_resp) => {
        if (_resp?.data?.Result === "SUCCESS") {
            addToast({ title: "Participant Created", color:'success' });
            onResp("close", null);
        } else {
            addToast({
                title: "Could not add member",
                description: "Please check the details and try again.",
            });
            console.error("Create user failed:", _resp?.data);
        }
    }).catch((_resp) => {
        console.error(_resp);
        addToast({
            title: "Error",
            description: "Something went wrong while creating the member.",
        });
    }).finally(() => {
        setCreating(false);
    })
}

return (<>
<Drawer key="blur" isOpen={show} onOpenChange={(isOpen) => {if (!isOpen) onResp("close", null);}} classNames={{ backdrop: "bg-black/30" }} backdrop={"blur"}>
    <DrawerContent>
        <DrawerHeader className="flex items-center gap-3">
            <Avatar key={avatarSeed} name={avatarName} size="sm" className="w-6 h-6" radius="full" src={`https://api.dicebear.com/9.x/thumbs/svg?seed=${avatarSeed}`}/>
            <span className="font-medium">New Connection</span>
        </DrawerHeader>

        <DrawerBody className="pb-6">
            <Card className="mt-2 shadow-sm">
                <CardHeader className="pb-0" />
                <CardBody className="flex flex-col gap-4">

                <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
                    <span className="text-xs font-medium tracking-wide">First Name *</span>
                    </label>
                    <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    type="text"
                    placeholder={dummyName.split(" ")[0]}
                    size="lg"
                    variant="bordered"
                    classNames={{
                        inputWrapper: `
                        bg-white border border-neutral-200 hover:bg-neutral-50 rounded-xl shadow-sm
                        dark:bg-content1/70 dark:border-default-200/60 dark:hover:bg-content2/60 dark:shadow-none
                        focus:outline-none focus:ring-0
                        data-[focus=true]:ring-0 data-[focus=true]:outline-none data-[focus=true]:border-neutral-300
                        `,
                        input: `
                        text-neutral-900 placeholder:text-neutral-400 text-base
                        dark:text-foreground focus:outline-none focus:ring-0
                        `,
                    }}
                    />
                </div>

                {/* Last Name */}
                <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
                    <span className="text-xs font-medium tracking-wide">Last Name *</span>
                    </label>
                    <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    type="text"
                    placeholder={dummyName.split(" ")[1]}
                    size="lg"
                    variant="bordered"
                    classNames={{
                        inputWrapper: `
                        bg-white border border-neutral-200 hover:bg-neutral-50 rounded-xl shadow-sm
                        dark:bg-content1/70 dark:border-default-200/60 dark:hover:bg-content2/60 dark:shadow-none
                        focus:outline-none focus:ring-0
                        data-[focus=true]:ring-0 data-[focus=true]:outline-none data-[focus=true]:border-neutral-300
                        `,
                        input: `
                        text-neutral-900 placeholder:text-neutral-400 text-base
                        dark:text-foreground focus:outline-none focus:ring-0
                        `,
                    }}
                    />
                </div>

                {/* Email */}
                <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
                    <span className="text-xs font-medium tracking-wide">Email *</span>
                    </label>
                    <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    placeholder={dummyMail}
                    size="lg"
                    variant="bordered"
                    classNames={{
                        inputWrapper: `
                        bg-white border border-neutral-200 hover:bg-neutral-50 rounded-xl shadow-sm
                        dark:bg-content1/70 dark:border-default-200/60 dark:hover:bg-content2/60 dark:shadow-none
                        focus:outline-none focus:ring-0
                        data-[focus=true]:ring-0 data-[focus=true]:outline-none data-[focus=true]:border-neutral-300
                        `,
                        input: `
                        text-neutral-900 placeholder:text-neutral-400 text-base
                        dark:text-foreground focus:outline-none focus:ring-0
                        `,
                    }}
                    />
                </div>

                {/* Mobile */}
                <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
                    <span className="text-xs font-medium tracking-wide">Mobile (optional)</span>
                    </label>
                    <Input
                    value={mobileDisplay}
                    onChange={(e) => handleMobileChange(e.target.value)}
                    type="tel"
                    placeholder="+27 82 123 4567"
                    size="lg"
                    variant="bordered"
                    classNames={{
                        inputWrapper: `
                        bg-white border border-neutral-200 hover:bg-neutral-50 rounded-xl shadow-sm
                        dark:bg-content1/70 dark:border-default-200/60 dark:hover:bg-content2/60 dark:shadow-none
                        focus:outline-none focus:ring-0
                        data-[focus=true]:ring-0 data-[focus=true]:outline-none data-[focus=true]:border-neutral-300
                        `,
                        input: `
                        text-neutral-900 placeholder:text-neutral-400 text-base
                        dark:text-foreground focus:outline-none focus:ring-0
                        `,
                    }}
                    />
                </div>
                </CardBody>
            </Card>
        </DrawerBody>

        <DrawerFooter className="gap-3">
            <Button onPress={() => onResp("close", null)} className="w-full" variant="flat" isDisabled={creating}>
                Cancel
            </Button>
            <Button onPress={createMember} className="w-full" color="primary" isLoading={creating} isDisabled={!isValid || creating} >
                Create
            </Button>
        </DrawerFooter>
    </DrawerContent>
</Drawer>
</>)
}

export default CreateConnectionDrawer